import {FC, useCallback, useEffect, useMemo} from "react";
import {Box, LinearProgress, Typography, useTheme} from "@mui/material";
import {
	DataGridPro,
	GridSelectionModel,GridToolbar
} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {useGetCohortSettings, type UserListDto, type UsersControllerListParams} from "@/app2/shared/data";
import {AdminTableColumnsPanel} from "@/components/common/table/AdminTableColumnsPanel";
import {useOwnedCohortId} from "@/app2/shared/hooks";
import {useUsersListGrid} from "@/app2/pages/ManagerView/Pages/Users/AdminUsersList/hooks/useUsersListGrid";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import {serverSideDateTimeFilterPanelProps} from "@/app2/shared/dataGrid/serverSideDateTimeColumnFilters";
import {getUsersListHeadCells} from "@/app2/pages/ManagerView/Pages/Users/AdminUsersList/getUsersListHeadCells";

const useStyles = makeStyles()((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1)
	},
	dataGrid: {
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	},
	selectionSummary: {
		fontWeight: 600
	}
}));

interface SpecificAudienceSelectorProps {
	/** Controlled list of selected user IDs. */
	selectedUserIds: number[];
	onChange: (userIds: number[]) => void;
	/** When true (already delivered) the grid is read-only. */
	disabled?: boolean;
}

export const SpecificAudienceSelector: FC<SpecificAudienceSelectorProps> = ({
	selectedUserIds,
	onChange,
	disabled = false
}) => {
	const {classes} = useStyles();

	const defaultFilterSettings: UsersControllerListParams = {
		page: 1,
		pageSize: 25,
		sortBy: "lastname",
		sortDirection: "asc"
	};
	const {
		const: {filterModel},
		api: {assignFilters}
	} = useCustomDataGrid<UserListDto>({
		defaultFilterSettings
	});

	/** No filter localStorage for this grid — list users API does not support persisted filters here. */
	const {
		rows,
		rowCount,
		isLoading,
		isFetched,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		sortModel,
		onSortModelChange
	} = useUsersListGrid({initialFilterSettings: defaultFilterSettings});

	/** Legacy: filters were accidentally stored under localStorage[""] when no storage key was set. */
	useEffect(() => {
		try {
			localStorage.removeItem("");
		} catch {
			/* ignore */
		}
	}, []);

	const rowsForGrid = useMemo(() => (isFetched ? rows : []), [isFetched, rows]);


	const handleSelectionChange = useCallback(
		(model: GridSelectionModel) => {
			if (!disabled) {
				onChange(model as number[]);
			}
		},
		[disabled, onChange]
	);
	const theme = useTheme();
	const cohortId = useOwnedCohortId();
	const {data: cohortSettings} = useGetCohortSettings(cohortId ?? 0, {
		enabled: !!cohortId
	});
	const columns = getUsersListHeadCells(theme, cohortSettings)

	return (
		<Box className={classes.root}>
			<Typography variant="body2" className={classes.selectionSummary}>
				{selectedUserIds.length === 0
					? "No employees selected"
					: `${selectedUserIds.length} employee${selectedUserIds.length === 1 ? "" : "s"} selected`}
			</Typography>
			<DataGridPro<UserListDto>
				autoHeight
				className={classes.dataGrid}
				loading={isLoading}
				sortingMode="server"
				filterMode="server"
				filterModel={filterModel}
				paginationMode="server"
				pagination
				page={page}
				pageSize={pageSize}
				rowCount={rowCount}
				sortModel={sortModel}
				rows={rowsForGrid}
				columns={columns.map(e => assignFilters(e as any))}
				checkboxSelection
				keepNonExistentRowsSelected
				isRowSelectable={() => !disabled}
				selectionModel={selectedUserIds}
				onSelectionModelChange={handleSelectionChange}
				onPageChange={onPageChange}
				onPageSizeChange={onPageSizeChange}
				onFilterModelChange={onFilterChange}
				onSortModelChange={onSortModelChange}
				components={{Toolbar: GridToolbar, ColumnsPanel: AdminTableColumnsPanel}}
				componentsProps={{filterPanel: serverSideDateTimeFilterPanelProps}}
				rowsPerPageOptions={[25, 50, 100, 200]}
				initialState={{
					columns: {
						columnVisibilityModel: {
							createdAt: false,
							phone: false,
							workload: false,
							email: false,
							timeSpentOnTraining: false,
							customFieldValue1: false,
							customFieldValue2: false,
							customFieldValue3: false,
							employeeCode: false,
							completedModules: false
						}
					}
				}}
				getRowId={(row) => row.id}
				sx={{"& .MuiDataGrid-row": {cursor: disabled ? "default" : "pointer"}}}
			/>
			{isLoading && <LinearProgress />}
		</Box>
	);
};
